import { Event, Platform } from '@fullstory/proto/fs/api/segment';
import {
  CreateUnnamedFunnelRequest,
  FunnelDetail,
  GetAllFunnelsResponse,
  GetUnnamedFunnelResponse,
  UnnamedFunnel,
} from '@fullstory/proto/fs/services/dx-objects';
import { Funnel } from '@fullstory/proto/fs/services/search-settings';
import { Duration } from '@fullstory/proto/google/protobuf';
import { createRequestReducer, DispatchedAction, RequestAction } from '@fullstory/redux-util';
import { combineReducers, createAppCacheReducer } from '@fullstory/redux-util/utils';
import { fetchAction } from '@fullstory/redux-util/utils/fetchStatus';
import {
  dependentTypesToDependentState,
  eventTypeToEventState,
  SearchGroup,
} from '@fullstory/search-model';
import { EventType } from '@fullstory/search-types';
import { mapKeys } from 'lodash';
import { Action } from 'redux';
import { getObjectWithOverlayStorageId } from '../../utils';
import { createAccessReducer } from '../../utils/accessControl';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { DashboardActions } from '../dashboards';
import { HomeRequestActions, HomeRequestTypes } from '../home';
import { SearchActions } from '../search';
import { SearchActionTypes } from '../search/types';
import { FunnelCreateActions, FunnelsActions } from './actions';
import {
  FunnelCounts,
  FunnelCreateTypes,
  FunnelMetricsDataType,
  FunnelsById,
  FunnelsRequestTypes,
  FunnelsState,
} from './types';
import { SpaceActions, SpaceRequestTypes } from '../spaces';
import { UniqueAggregation } from '@fullstory/proto/fs/api/metrics';
import { BulkActionsRequestTypes } from '../bulkActions/types';
import { BulkRequestActions } from '../bulkActions/actions';

const getFunnelId = (funnel: FunnelDetail | null | undefined): string | undefined =>
  funnel?.funnel?.funnel?.id;

const funnelCountStatus = fetchAction({
  loading: FunnelCreateTypes.FetchFunnelData,
  failed: FunnelCreateTypes.FetchFunnelDataFailed,
  loaded: FunnelCreateTypes.SetFunnelData,
});

type FunnelCreateModalState = {
  showModal: ReturnType<typeof showModal>;
  selectedFunnel: ReturnType<typeof selectedFunnel>;
  funnelCounts: ReturnType<typeof funnelCounts>;
  funnelCountStatus: ReturnType<typeof funnelCountStatus>;
};

// contains all the state related to creating and validating a funnel
export const funnelCreateModal = combineReducers<FunnelCreateModalState>({
  showModal,
  selectedFunnel,
  funnelCounts,
  funnelCountStatus,
});

function showModal(state: boolean = false, action: FunnelCreateActions): boolean {
  switch (action.type) {
    case FunnelCreateTypes.ShowModal:
      return action.show;
  }
  return state;
}

function selectedFunnel(state: Funnel | null = null, action: FunnelCreateActions): Funnel | null {
  switch (action.type) {
    case FunnelCreateTypes.ResetModal:
      return null;
    case FunnelCreateTypes.SetSelected:
      return {
        ...action.funnel,
      };
  }
  return state;
}

function funnelCounts(state: FunnelCounts[] = [], action: FunnelCreateActions): FunnelCounts[] {
  switch (action.type) {
    case FunnelCreateTypes.ResetModal:
      return [];
    case FunnelCreateTypes.SetFunnelData:
      return action.data;
  }
  return state;
}

// Force FunnelDetail rsp to conform to latest client EventSequence/Constraints model
const initFunnel = (funnelDetail: FunnelDetail): FunnelDetail => {
  const es = funnelDetail.funnel?.funnel?.events;
  if (es) {
    funnelDetail.funnel!.funnel!.events = {
      events: es.events ?? [],
      constraints: {
        inOrder: es.constraints?.inOrder ?? false,
        inSameSession: es.constraints?.inSameSession ?? false,
        platform: es.constraints?.platform ?? Platform.PLATFORM_INVALID,
        timeRange: es.constraints?.timeRange ?? null,
        completeSessions: es.constraints?.completeSessions ?? false,
        withinTime: es.constraints?.withinTime ?? Duration.zero,
        eventThresholds: es.constraints?.eventThresholds ?? [],
      },
    };
  }
  return funnelDetail;
};

export const funnels = (
  state: FunnelsState = {},
  action:
    | FunnelsActions
    | DashboardActions
    | HomeRequestActions
    | AppBackendActions
    | SpaceActions
    | BulkRequestActions,
): FunnelsState => {
  switch (action.type) {
    case FunnelsRequestTypes.GetAllFunnelsSuccess: {
      return action.data.funnels.reduce<FunnelsById>(
        (acc, val) => {
          const id = getFunnelId(val);

          if (id != null) acc[id] = initFunnel(val);

          return acc;
        },
        { ...state },
      );
    }
    case HomeRequestTypes.DuplicateHomeCardSuccess:
    case FunnelsRequestTypes.GetFunnelSuccess: {
      const { funnel } = action.data;
      const id = getFunnelId(funnel);

      return funnel == null || id == null ? state : { ...state, [id]: initFunnel(funnel) };
    }
    case FunnelsRequestTypes.SetFunnelSuccess: {
      const { funnel } = action.data;
      const id = getFunnelId(funnel);

      return funnel == null || id == null ? state : { ...state, [id]: initFunnel(funnel) };
    }
    case FunnelsRequestTypes.MarkViewedSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const { lastViewed } = action.data;
      const { funnelId } = body;
      const funnel = state?.[funnelId];

      return funnel == null ? state : { ...state, [funnelId]: { ...funnel, lastViewed } };
    }
    case FunnelsRequestTypes.StarFunnelSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const { funnelId, starred } = body;
      const funnel = state?.[funnelId];

      return funnel == null ? state : { ...state, [funnelId]: { ...funnel, starred } };
    }
    case FunnelsRequestTypes.ArchiveFunnelSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const { funnelId, archived } = body;
      const funnel = state?.[funnelId];
      const funnelRecord = funnel?.funnel;
      const metadata = funnelRecord?.metadata;

      return funnel == null || funnelRecord == null || metadata == null
        ? state
        : {
            ...state,
            [funnelId]: {
              ...funnel,
              funnel: {
                ...funnelRecord,
                metadata: { ...metadata, archived },
              },
            },
          };
    }
    case BulkActionsRequestTypes.BulkArchiveObjectsSuccess: {
      const { body } = action.request;

      if (!body) return state;

      let newState = { ...state };
      const funnelsReq = body.funnels;

      if (funnelsReq != null) {
        funnelsReq.funnels.forEach(m => {
          const { funnelId, archived } = m;
          const funnel = newState?.[funnelId];
          const funnelRecord = funnel?.funnel;
          const metadata = funnelRecord?.metadata;

          if (funnel && funnelRecord && metadata) {
            newState = {
              ...newState,
              [funnelId]: {
                ...funnel,
                funnel: {
                  ...funnelRecord,
                  metadata: { ...metadata, archived },
                },
              },
            };
          }
        });
      }

      return newState;
    }
    case SpaceRequestTypes.GetSpaceCardViewSuccess: {
      const updated = { ...state };
      const allObjects = action.data.sections.flatMap(s => s.cards.flatMap(c => c.object));

      for (const object of allObjects) {
        if (object?.funnel && object.funnel.funnel?.funnel?.id) {
          updated[object.funnel.funnel.funnel.id] = object.funnel;
        }
      }

      return updated;
    }
    case SpaceRequestTypes.GetLibraryViewSuccess: {
      const funnels = action.data.objects.reduce<FunnelsState>((acc, o) => {
        if (o.funnel?.funnel?.funnel?.id) acc[o.funnel.funnel?.funnel?.id] = o.funnel;
        return acc;
      }, {});

      return Object.keys(funnels).length
        ? {
            ...state,
            ...funnels,
          }
        : state;
    }
    case AppBackendRequestTypes.GetDashboardDetailViewSuccess:
    case AppBackendRequestTypes.GetFunnelDetailViewSuccess:
      if (!action.data.deps?.funnels) return state;
      if (action.data.overlay && action.data.deps.funnels) {
        const funnels = action.data.deps.funnels;
        const overlayId = action.data.overlay.id;
        const updatedFunnels = mapKeys(funnels, (_, key) =>
          getObjectWithOverlayStorageId(key, overlayId),
        );
        return { ...state, ...updatedFunnels };
      }
      return { ...state, ...action.data.deps.funnels };
    case AppBackendRequestTypes.GetHomeViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetRetentionMetricDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetSidebarSuccess: {
      if (!action.data.deps?.funnels) return state;
      return { ...state, ...action.data.deps?.funnels };
    }
  }
  return state;
};

const funnelMetrics = createAppCacheReducer<FunnelMetricsDataType>()(
  FunnelsRequestTypes.FunnelAppCacheIsLoading,
  FunnelsRequestTypes.FunnelAppCacheLoadFailed,
  FunnelsRequestTypes.FunnelAppCacheRefetching,
  FunnelsRequestTypes.FunnelAppCacheHasData,
  FunnelsRequestTypes.FunnelAppCacheFresh,
  FunnelsRequestTypes.FunnelAppCacheSwap,
  FunnelsRequestTypes.FunnelAppCacheClear,
)();

const allFunnels = createRequestReducer<GetAllFunnelsResponse>()(
  FunnelsRequestTypes.GetAllFunnelsRequest,
  FunnelsRequestTypes.GetAllFunnelsSuccess,
  FunnelsRequestTypes.GetAllFunnelsFailure,
);

type UnnamedFunnelsState = { [hash: string]: UnnamedFunnel };
const unnamedFunnelsRequests = createRequestReducer<UnnamedFunnelsState, GetUnnamedFunnelResponse>({
  mergeData: (state, update) => {
    const { hash, funnel } = update;
    if (!funnel) return state;

    return {
      ...state,
      [hash]: funnel,
    };
  },
})(
  FunnelsRequestTypes.GetUnnamedFunnelRequest,
  FunnelsRequestTypes.GetUnnamedFunnelSuccess,
  FunnelsRequestTypes.GetUnnamedFunnelFailure,
);

type CreateUnnamedFunnelRequestAction = RequestAction<
  FunnelsRequestTypes.CreateUnnamedFunnelRequest,
  CreateUnnamedFunnelRequest
>;
export const isCreateUnnamedFunnelRequestAction = (
  action: Action,
): action is CreateUnnamedFunnelRequestAction =>
  (action as CreateUnnamedFunnelRequestAction).type ===
  FunnelsRequestTypes.CreateUnnamedFunnelRequest;

type UnnamedFunnelsRequestState = ReturnType<typeof unnamedFunnelsRequests>;
const unnamedFunnels = (
  state: UnnamedFunnelsRequestState = {
    lastRequested: -1,
    lastResponded: -1,
    loading: false,
    error: false,
    data: null,
    safeMessage: null,
  },
  action: CreateUnnamedFunnelRequestAction | DispatchedAction<any, any, any, any, any>,
): UnnamedFunnelsRequestState => {
  if (isCreateUnnamedFunnelRequestAction(action)) {
    if (!action.body || !action.body.funnel) return state;
    return {
      ...state,
      data: {
        ...state.data,
        [action.body.hash]: action.body.funnel,
      },
    };
  }
  return unnamedFunnelsRequests(state, action);
};

const access = createAccessReducer(
  FunnelsRequestTypes.GetFunnelsAccessRequest,
  FunnelsRequestTypes.GetFunnelsAccessSuccess,
  FunnelsRequestTypes.GetFunnelsAccessFailure,
);

const omnisearch = (
  state: FunnelDetail | null = null,
  action: SearchActions,
): FunnelDetail | null => {
  switch (action.type) {
    case SearchActionTypes.AddRow:
      if (action.mode !== 'funnel') return state;
      return funnelWithEvent(eventTypeToEventState(action.data as SearchGroup<EventType>));
    case SearchActionTypes.AppendDependents:
      if (action.mode !== 'funnel') return state;
      return funnelWithEvent({
        primary: null,
        deps: dependentTypesToDependentState(action.data),
      });
  }
  return state;
};

const funnelWithEvent = (event: Event): FunnelDetail => ({
  funnel: {
    funnel: {
      id: '',
      name: '',
      description: '',
      events: {
        events: [
          event,
          {
            primary: null,
            deps: [],
          },
        ],
        constraints: null,
      },
      funnelSettings: null,
      aggregation: UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL,
    },
    metadata: null,
    isDcmCreated: false,
  },
  starred: false,
  lastViewed: null,
});

type FunnelReducerState = {
  funnelCreateModal: ReturnType<typeof funnelCreateModal>;
  allFunnels: ReturnType<typeof allFunnels>;
  funnels: ReturnType<typeof funnels>;
  funnelMetrics: ReturnType<typeof funnelMetrics>;
  unnamedFunnels: ReturnType<typeof unnamedFunnels>;
  access: ReturnType<typeof access>;
  omnisearch: ReturnType<typeof omnisearch>;
};

export const funnelReducer = combineReducers<FunnelReducerState>({
  funnelCreateModal,
  allFunnels,
  funnels,
  funnelMetrics,
  unnamedFunnels,
  access,
  omnisearch,
});
